// Нативные возможности Android/iOS-оболочки. В браузере всё это не вызывается:
// каждая функция сначала проверяет, что мы внутри Capacitor-приложения.
import { isNativeApp, nativePlatform } from "./platform";

/** Сохранение/отправка готового изображения через системное меню Android и iOS. */
export async function nativeShareBlob(blob, filename, title = "ANGIOLINI") {
  if (!isNativeApp()) return false;
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.readAsDataURL(blob);
  });
  const written = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });
  await Share.share({ title, files: [written.uri] });
  return true;
}

// ───────────────────────── Push-уведомления (Android) ─────────────────────────
// iOS-сборка в App Store выпущена без APNs-capability, поэтому нативный push
// включаем только в Android; iOS и браузер продолжают жить на веб-пуше.
const pushSupported = () => isNativeApp() && nativePlatform() === "android";

async function loadPush() {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  return PushNotifications;
}

async function ensureChannel(PushNotifications) {
  // Android 8+: без канала уведомление не покажется
  try {
    await PushNotifications.createChannel({
      id: "angiolini_default",
      name: "ANGIOLINI VIP CLUB",
      description: "Новости, бонусы и статус заказов",
      importance: 4,
      visibility: 1,
      lights: true,
      lightColor: "#C9A44C",
      vibration: true,
    });
  } catch { /* канал уже создан */ }
}

/** Текущее состояние разрешения: "granted" | "denied" | "prompt" | "web". */
export async function nativePushPermission() {
  if (!pushSupported()) return "web";
  try {
    const PushNotifications = await loadPush();
    const perm = await PushNotifications.checkPermissions();
    return perm?.receive || "prompt";
  } catch {
    return "web";
  }
}

/** Переход пользователя в системные настройки уведомлений приложения. */
export async function openAppNotificationSettings() {
  if (!isNativeApp()) return false;
  try {
    const { registerPlugin } = await import("@capacitor/core");
    const AppSettings = registerPlugin("AppSettings");
    await AppSettings.openNotifications();
    return true;
  } catch {
    return false;
  }
}

/** Обработка нажатия на уведомление: открываем нужный экран приложения. */
async function attachTapHandler(PushNotifications) {
  await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const url = action?.notification?.data?.url;
    if (typeof url === "string" && url.startsWith("/")) {
      window.location.assign(url);
    }
  });
}

/**
 * Регистрация нативных push: разрешение → токен FCM → колбэк для отправки на сервер.
 * requestIfNeeded=false — только тихая проверка уже выданного разрешения.
 * Возвращает "granted" | "denied" | "prompt" | "error" | "web".
 */
export async function registerNativePush(onToken, { requestIfNeeded = true } = {}) {
  if (!pushSupported()) return "web";
  try {
    return await registerNativePushInner(onToken, requestIfNeeded);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[push] register failed", e);
    return "error";
  }
}

async function registerNativePushInner(onToken, requestIfNeeded) {
  const PushNotifications = await loadPush();
  await ensureChannel(PushNotifications);

  let perm = await PushNotifications.checkPermissions();
  if (perm?.receive !== "granted") {
    if (!requestIfNeeded) return perm?.receive || "prompt";
    // Android 13+: системный диалог POST_NOTIFICATIONS
    perm = await PushNotifications.requestPermissions();
  }
  if (perm?.receive !== "granted") return perm?.receive === "denied" ? "denied" : "prompt";

  await PushNotifications.removeAllListeners();
  const token = await new Promise((resolve) => {
    let done = false;
    const finish = (value) => { if (!done) { done = true; resolve(value); } };
    const timer = setTimeout(() => finish(null), 15000);
    (async () => {
      await PushNotifications.addListener("registration", (t) => {
        clearTimeout(timer);
        finish(t?.value || null);
      });
      await PushNotifications.addListener("registrationError", (e) => {
        clearTimeout(timer);
        // eslint-disable-next-line no-console
        console.warn("[push] registration error", e);
        finish(null);
      });
      await PushNotifications.register();
    })();
  });

  await attachTapHandler(PushNotifications);
  if (!token) return "error";
  const saved = await onToken(token);
  return saved === false ? "error" : "granted";
}

/** Отписка устройства: снимаем слушатели и чистим шторку. Токен удаляет backend. */
export async function stopNativePush() {
  if (!pushSupported()) return false;
  try {
    const PushNotifications = await loadPush();
    await PushNotifications.removeAllListeners();
    await PushNotifications.removeAllDeliveredNotifications();
    return true;
  } catch {
    return false;
  }
}

/** Признак того, что страница открыта внутри Android-оболочки приложения. */
export const isAndroidShell = () =>
  typeof navigator !== "undefined" && /;\s?wv\)/.test(navigator.userAgent || "");

/**
 * Полное состояние push в текущей сборке — для диагностики релиза на телефоне.
 * Ничего не меняет, только читает.
 */
export async function collectPushDiagnostics() {
  const d = {
    origin: window.location.origin,
    platform: nativePlatform(),
    isNative: isNativeApp(),
    androidBridge: typeof window.androidBridge !== "undefined",
    capacitorGlobal: typeof window.Capacitor !== "undefined",
    androidShellUA: isAndroidShell(),
    pluginPush: false,
    pluginSettings: false,
    permission: "—",
    token: "нет",
    theme: document.documentElement.getAttribute("data-ang-theme") || "—",
    background: getComputedStyle(document.body).backgroundColor,
    systemDark: window.matchMedia("(prefers-color-scheme: dark)").matches,
    webNotificationApi: typeof Notification !== "undefined",
    ua: (navigator.userAgent || "").slice(0, 140),
  };
  try {
    const { Capacitor } = await import("@capacitor/core");
    d.pluginPush = Capacitor.isPluginAvailable("PushNotifications");
    d.pluginSettings = Capacitor.isPluginAvailable("AppSettings");
  } catch (e) {
    d.error = String(e).slice(0, 100);
  }
  if (d.pluginPush) {
    try {
      const PushNotifications = await loadPush();
      d.permission = (await PushNotifications.checkPermissions())?.receive || "—";
    } catch (e) {
      d.permission = "ошибка: " + String(e).slice(0, 80);
    }
  }
  try {
    const t = localStorage.getItem("angiolini_fcm_token");
    if (t) d.token = `${t.slice(0, 12)}… (${t.length} симв.)`;
  } catch { /* noop */ }
  return d;
}

/** Системная кнопка «Назад» на Android: шаг назад по приложению, с главной — свернуть. */
export async function attachBackButton(handler) {
  if (!isNativeApp() || nativePlatform() !== "android") return () => {};
  const { App } = await import("@capacitor/app");
  const sub = await App.addListener("backButton", handler);
  return () => { try { sub.remove(); } catch { /* noop */ } };
}
